import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';
import { useLocation } from '@reach/router';
import { isProdEnv } from '@pcf-engineering/pcs-ui-kit/utils';

const query = graphql`
  query SiteMetadata {
    site {
      siteMetadata {
        siteUrl
        title
        description
        author
      }
      buildTime
    }
  }
`;

/**
 *  SEO - Renders SEO metadata for a page.
 * @param {String} title Optional page title.
 * @param {String} description Optional page meta description.
 * @param {Object} location Location object from gatsby.
 * @param {Array} meta Optional array of metadata to render as <meta> tags.
 * @param {Array} breadcrumbs Optional array of breadcrumbs to render as ld+json schema data.
 * @param {Array} ldJson Optional array of metadata to render as ld+json schema data.
 * @param {Boolean} noIndex Whether or not the page should be indexable by search crawlers.
 * @param {Boolean} noLocalization Whether or not the page has a french language option available.
 */
const SEO = ({
  title,
  description,
  metaTags,
  linkTags,
  ldJson,
  noIndex,
  noLocalization,
}: {
  title: string;
  description?: string;
  metaTags?: Array<Record<string, string>>;
  linkTags?: Array<Record<string, string>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ldJson?: Array<Record<string, any>>;
  noIndex?: boolean;
  noLocalization?: boolean;
}) => {
  const { locale } = useIntl();
  const { pathname: pathName } = useLocation();
  const { site: { siteMetadata, buildTime } } = useStaticQuery(query);
  const { author, siteUrl } = siteMetadata;

  const metaDescription = description || siteMetadata.description;
  const htmlClass = pathName.replace(locale, '').replace(/\//g, '');
  const altLocale = locale === 'en' ? 'fr' : 'en';
  const currentPath = pathName.replace(/(\w)([?#][\s\S]+)*$/i, '$1/$2');
  const altPath = currentPath.replace(`/${locale}/`, `/${altLocale}/`);

  const meta = [
    { name: 'description', content: metaDescription },
    { property: 'og:title', content: title },
    { property: 'og:description', content: metaDescription },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:creator', content: author },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: metaDescription },
    { name: 'build-date', content: buildTime },
    ...(metaTags ?? []),
  ];

  const link = [
    { rel: 'alternate', hrefLang: `${locale}-CA`, href: `${siteUrl}${currentPath}` },
    ...(linkTags ?? []),
  ];

  if (!isProdEnv(process.env.GATSBY_DEPLOY_ENV) || noIndex) {
    meta.unshift({ name: 'robots', content: 'noindex' });
  }

  link.unshift({ rel: 'canonical', href: `${siteUrl}${currentPath}` });

  if (!noLocalization) {
    link.push({ rel: 'alternate', hrefLang: `${altLocale}-CA`, href: `${siteUrl}${altPath}` });
  }
  return (
    <Helmet
      htmlAttributes={{ lang: locale, class: htmlClass }}
      title={title}
      meta={meta}
      link={link}
    >
      {ldJson && (
        <script type="application/ld+json">
          {JSON.stringify(ldJson, null, 2)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
